<template>
  <div class="reporting-main">
    <!-- Toolbar (hors du scroll pour que les dropdowns ne soient pas clippés) -->
    <div class="reporting-toolbar" v-if="hasPages">
        <ReportingDatePicker
          :date-start="dateStart"
          :date-end="dateEnd"
          :compare-mode="compareMode"
          @apply="onDatePickerApply"
        />

        <button class="search-btn" :disabled="loading || !selectedPageIds.length" @click="loadData(false)">
          <i class="material-icons">{{ loading ? 'hourglass_empty' : 'search' }}</i>
          {{ $t('dash.reporting.search') }}
        </button>

        <button
          v-if="currentStats || pageStats.length"
          class="refresh-btn"
          :disabled="loading"
          :title="$t('dash.reporting.force_refresh')"
          @click="loadData(true)"
        >
          <i class="material-icons">refresh</i>
        </button>

        <div
          v-if="(!isComparisonMode && currentStats) || (isComparisonMode && detailPageId && detailStats)"
          class="export-dropdown"
          :class="{ open: exportDropdownOpen }"
          v-click-outside="closeExportDropdown"
        >
          <button class="export-btn" :disabled="pdfLoading || apiUrlLoading" @click="exportDropdownOpen = !exportDropdownOpen">
            <i class="material-icons">{{ (pdfLoading || apiUrlLoading) ? 'hourglass_empty' : 'file_download' }}</i>
            {{ $t('dash.reporting.export') }}
            <i class="material-icons export-caret">expand_more</i>
          </button>
          <div class="export-menu" v-show="exportDropdownOpen">
            <button class="export-item" @click="runExport('pdf')">
              <i class="material-icons">picture_as_pdf</i> PDF
            </button>
            <button class="export-item" @click="runExport('csv')">
              <i class="material-icons">table_chart</i> CSV
            </button>
            <button class="export-item" @click="runExport('excel')">
              <i class="material-icons">grid_on</i> Excel
            </button>
            <div class="export-separator"></div>
            <button class="export-item" @click="runExport('api')">
              <i class="material-icons">link</i> {{ $t('dash.reporting.api_url') }}
            </button>
          </div>
        </div>
      </div>

    <!-- Zone scrollable -->
    <div class="reporting-content">

      <!-- Modal API URL -->
      <div class="api-modal-backdrop" v-if="apiModal.open" @click.self="apiModal.open = false">
        <div class="api-modal">
          <div class="api-modal-header">
            <span>{{ $t('dash.reporting.api_url_title') }}</span>
            <button class="api-modal-close" @click="apiModal.open = false"><i class="material-icons">close</i></button>
          </div>
          <div class="api-modal-body">
            <p class="api-modal-desc">{{ $t('dash.reporting.api_url_desc') }}</p>
            <div v-if="apiModal.loading" class="api-modal-loading">
              <i class="material-icons spin">hourglass_empty</i>
            </div>
            <template v-else-if="apiModal.url">
              <div class="api-url-field">
                <input type="text" readonly :value="apiModal.url" class="api-url-input" ref="apiUrlInput">
                <button class="api-url-copy" @click="copyApiUrl" :title="$t('dash.reporting.api_url_copy')">
                  <i class="material-icons">{{ apiModal.copied ? 'check' : 'content_copy' }}</i>
                </button>
              </div>
              <p class="api-url-hint">{{ $t('dash.reporting.api_url_hint') }}</p>
            </template>
            <p v-else-if="apiModal.error" class="api-modal-error">{{ apiModal.error }}</p>
          </div>
        </div>
      </div>

      <!-- Progress -->
      <div class="reporting-progress" v-if="loading">
        <div class="progress-bar-track">
          <div class="progress-bar-fill" :style="{ width: progressPercent + '%' }"></div>
        </div>
        <span class="progress-label" v-if="progressDay">{{ progressDay }}</span>
      </div>

      <!-- Empty state -->
      <div class="reporting-empty" v-if="!selectedPageIds.length">
        <i class="material-icons">bar_chart</i>
        <p>{{ $t('dash.reporting.select_page') }}</p>
      </div>

      <!-- Comparison mode -->
      <template v-else-if="isComparisonMode && pageStats.length">

        <!-- Barre d'onglets — toujours en tête -->
        <div class="comparison-tab-bar">
          <div
            class="ctab ctab--global"
            :class="{ active: !detailPageId }"
            @click="detailPageId = null"
          >
            <i class="material-icons ctab-icon">dashboard</i>
            <span class="ctab-label">{{ $t('dash.reporting.comparison') }}</span>
          </div>
          <div class="ctab-sep"></div>
          <div
            class="ctab"
            v-for="entry in pageStats"
            :key="entry.page.id"
            :class="{ active: entry.page.id === detailPageId }"
            @click="detailPageId = entry.page.id"
          >
            <div class="ctab-avatar-wrap">
              <img v-if="entry.page.avatar" :src="entry.page.avatar" class="ctab-avatar" alt="">
              <span v-else class="ctab-avatar-fallback">{{ (entry.page.name || '?')[0].toUpperCase() }}</span>
              <img v-if="networkIcon(entry.page.network)" :src="networkIcon(entry.page.network)" class="ctab-net-badge" alt="">
            </div>
            <span class="ctab-label">{{ entry.page.name }}</span>
          </div>
        </div>

        <!-- Vue globale -->
        <template v-if="!detailPageId">
          <ReportingComparison
            :pages-data="pageStats"
            :date-start="dateStart"
            :date-end="dateEnd"
            :sort-criterion="comparisonSortCriterion"
            :concurrent-data="concurrentDataCache"
            :prev-loading="prevLoading"
            :concurrents-loading="concurrentsLoading"
            @drill-down="detailPageId = $event"
          />
          <ReportingComparisonAggregate
            :pages-data="pageStats"
            :prev-loading="prevLoading"
            :sort-criterion.sync="comparisonSortCriterion"
          />
        </template>

        <!-- Détail d'une page -->
        <template v-else-if="detailPageId && detailStats">
          <ReportingOverview :stats="detailStats" :page="detailPage" :date-start="dateStart" :date-end="dateEnd" :prev-loading="prevLoading" />
          <ReportingVisibility :stats="detailStats" :graph-labels="detailGraphLabels" :page="detailPage" />
          <ReportingImpressions :stats="detailStats" :graph-labels="detailGraphLabels" :page="detailPage" />
          <ReportingEngagement :stats="detailStats" :graph-labels="detailGraphLabels" :page="detailPage" />
          <ReportingContent :stats="detailStats" :page="detailPage" />
          <ReportingPostTypes :stats="detailStats" :page="detailPage" />
          <ReportingVideos :stats="detailStats" :page="detailPage" />
          <ReportingStories :stats="detailStats" :page="detailPage" />
          <ReportingDemographics :stats="detailStats" :page="detailPage" />
          <ReportingConcurrent
            ref="concurrentSection"
            :page="detailPage"
            :stats="detailStats"
            :date-start="dateStart"
            :date-end="dateEnd"
          />
        </template>

      </template>

      <!-- Single page sections -->
      <template v-else-if="!isComparisonMode && currentStats">
        <ReportingOverview
          :stats="currentStats"
          :page="currentPage"
          :date-start="dateStart"
          :date-end="dateEnd"
          :prev-loading="prevLoading"
        />
        <ReportingVisibility
          :stats="currentStats"
          :graph-labels="graphLabels"
          :page="currentPage"
        />
        <ReportingImpressions
          :stats="currentStats"
          :graph-labels="graphLabels"
          :page="currentPage"
        />
        <ReportingEngagement
          :stats="currentStats"
          :graph-labels="graphLabels"
          :page="currentPage"
        />
        <ReportingContent
          :stats="currentStats"
          :page="currentPage"
        />
        <ReportingPostTypes
          :stats="currentStats"
          :page="currentPage"
        />
        <ReportingVideos
          :stats="currentStats"
          :page="currentPage"
        />
        <ReportingStories
          :stats="currentStats"
          :page="currentPage"
        />
        <ReportingDemographics
          :stats="currentStats"
          :page="currentPage"
        />
        <ReportingConcurrent
          ref="concurrentSection"
          :page="currentPage"
          :stats="currentStats"
          :date-start="dateStart"
          :date-end="dateEnd"
        />
      </template>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import vClickOutside from 'vue-click-outside'
import ReportingOverview from './ReportingOverview.vue'
import ReportingVisibility from './ReportingVisibility.vue'
import ReportingImpressions from './ReportingImpressions.vue'
import ReportingEngagement from './ReportingEngagement.vue'
import ReportingContent from './ReportingContent.vue'
import ReportingDemographics from './ReportingDemographics.vue'
import ReportingComparison from './ReportingComparison.vue'
import ReportingComparisonAggregate from './ReportingComparisonAggregate.vue'
import ReportingPostTypes from './ReportingPostTypes.vue'
import ReportingVideos from './ReportingVideos.vue'
import ReportingStories from './ReportingStories.vue'
import ReportingConcurrent from './ReportingConcurrent.vue'
import ReportingDatePicker from './ReportingDatePicker.vue'
import { EventBus } from '@/event-bus'

const SUPPORTED_NETWORKS = ['facebook', 'instagram', 'linkedin', 'tiktok']

export default {
  name: 'ReportingMain',
  directives: { clickOutside: vClickOutside },
  components: {
    ReportingOverview,
    ReportingVisibility,
    ReportingImpressions,
    ReportingEngagement,
    ReportingContent,
    ReportingDemographics,
    ReportingComparison,
    ReportingComparisonAggregate,
    ReportingPostTypes,
    ReportingVideos,
    ReportingStories,
    ReportingConcurrent,
    ReportingDatePicker,
  },

  data() {
    return {
      dateStart: moment().subtract(30, 'days').format('YYYY-MM-DD'),
      dateEnd: moment().format('YYYY-MM-DD'),
      compareMode: 'previous',

      loading: false,
      forceRefresh: false,
      pdfLoading: false,
      apiUrlLoading: false,
      exportDropdownOpen: false,
      apiModal: { open: false, loading: false, url: null, error: null, copied: false },
      progressPercent: 0,
      progressDay: null,

      currentStats: null,
      graphLabels: [],
      pageStats: [],
      detailPageId: null,
      prevLoading: false,
      comparisonSortCriterion: 'engagement',
      concurrentDataCache: {},
      concurrentsLoading: false,

      activeEventSources: [],
    }
  },

  computed: {
    allPages() {
      return this.$store.getters['selectPage/selectPageAllPages']
    },
    reportingAllPages() {
      return Object.fromEntries(
        Object.entries(this.allPages).filter(([, page]) => SUPPORTED_NETWORKS.includes(page.network))
      )
    },
    hasPages() {
      return Object.keys(this.reportingAllPages).length > 0
    },
    // IDs sélectionnés depuis le store global — filtrés sur les réseaux supportés
    selectedPageIds() {
      return this.$store.getters['selectPage/selectPagePageSelected']
        .filter(id => this.reportingAllPages[id])
    },
    isComparisonMode() {
      return this.selectedPageIds.length > 1
    },
    currentPage() {
      return this.selectedPageIds.length === 1
        ? this.reportingAllPages[this.selectedPageIds[0]]
        : null
    },
    today() {
      return moment().format('YYYY-MM-DD')
    },
    localeDateShort() {
      return this.$i18n.locale === 'en' ? 'MM/DD' : 'DD/MM'
    },
    localeDateLong() {
      return this.$i18n.locale === 'en' ? 'MM/DD/YYYY' : 'DD/MM/YYYY'
    },
    periodDays() {
      return moment(this.dateEnd).diff(moment(this.dateStart), 'days') + 1
    },
    effectiveCompareMode() {
      var days = this.periodDays
      if (this.compareMode === 'month' && days > 31) return 'previous'
      if (this.compareMode === 'year' && days > 365) return 'previous'
      return this.compareMode
    },
    detailPageEntry() {
      if (!this.detailPageId) return null
      return this.pageStats.find(e => e.page.id === this.detailPageId) || null
    },
    detailStats() {
      return this.detailPageEntry ? this.detailPageEntry.stats : null
    },
    detailPage() {
      return this.detailPageEntry ? this.detailPageEntry.page : null
    },
    detailGraphLabels() {
      if (!this.detailStats) return []
      return (this.detailStats.page.nb_post_graph || [])
        .sort((a, b) => a.date - b.date)
        .map(p => moment(p.date * 1000).format(this.localeDateLong))
    },
  },

  watch: {
    selectedPageIds(newIds, oldIds) {
      // allPages re-fetches create new array references even with same content — skip those
      const prev = oldIds || []
      if (newIds.length === prev.length && newIds.every((id, i) => id === prev[i])) return

      if (!newIds.length) {
        this.currentStats = null
        this.pageStats = []
        this.detailPageId = null
        return
      }

      // Mise à jour incrémentale si on a déjà des données de comparaison
      if (this.pageStats.length) {
        const loadedIds = new Set(this.pageStats.map(e => e.page.id))
        const removed = (oldIds || []).filter(id => !newIds.includes(id))
        const added = newIds.filter(id => !loadedIds.has(id))

        if (removed.length) {
          this.pageStats = this.pageStats.filter(e => !removed.includes(e.page.id))
          if (this.detailPageId && removed.includes(this.detailPageId)) {
            this.detailPageId = null
          }
        }
        if (added.length) {
          this.fetchPages(added)
        }

        // Passage de comparaison → page unique : réutiliser la donnée déjà chargée
        if (newIds.length === 1) {
          const entry = this.pageStats.find(e => e.page.id === newIds[0])
          if (entry) {
            this.currentStats = entry.stats
            this.graphLabels = (entry.stats.page.nb_post_graph || [])
              .sort((a, b) => a.date - b.date)
              .map(p => moment(p.date * 1000).format(this.localeDateLong))
            this.pageStats = []
            this.detailPageId = null
          }
        }
        return
      }

      // Passage de page unique → comparaison : conserver la donnée de la page déjà chargée
      if (this.currentStats && this.currentPage && newIds.includes(this.currentPage.id) && newIds.length > 1) {
        this.pageStats = [{ page: this.currentPage, stats: this.currentStats }]
        this.currentStats = null
        this.fetchPages(newIds.filter(id => id !== this.currentPage.id))
        return
      }

      // Cas général : reset, l'utilisateur clique Rechercher pour charger
      this.currentStats = null
      this.pageStats = []
      this.detailPageId = null
    },
  },

  methods: {

    networkIcon(network) {
      const map = {
        facebook:  require('@/assets/img/iconsNetwork/ico-facebook.png'),
        instagram: require('@/assets/img/iconsNetwork/ico-instagram.png'),
        linkedin:  require('@/assets/img/iconsNetwork/ico-linkedin.png'),
        tiktok:    require('@/assets/img/iconsNetwork/ico-tiktok.png'),
      }
      return map[network] || null
    },

    onDatePickerApply({ dateStart, dateEnd, compareMode }) {
      this.dateStart   = dateStart
      this.dateEnd     = dateEnd
      this.compareMode = compareMode
      this.loadData(false)
    },

    closeAllEventSources() {
      this.activeEventSources.forEach(es => es.close())
      this.activeEventSources = []
    },

    buildEndpoint(pageId) {
      const { VUE_APP_API_HOST } = process.env
      const since = moment(this.dateStart).unix()
      const until = moment(this.dateEnd).unix()
      const tz = new Date().getTimezoneOffset()
      const refresh = this.forceRefresh ? '&forceRefresh=1' : ''
      return `${VUE_APP_API_HOST}/reporting?pageid=${pageId}&since=${since}&until=${until}&timezone=${tz}&step=all&percent=true${refresh}`
    },

    loadData(forceRefresh) {
      this.forceRefresh = !!forceRefresh
      this.closeAllEventSources()
      this.loading = true
      this.prevLoading = false
      this.progressPercent = 0
      this.progressDay = null
      this.currentStats = null
      this.graphLabels = []
      this.pageStats = []
      this.detailPageId = null
      this.concurrentDataCache = {}
      this.concurrentsLoading = false

      if (this.isComparisonMode) {
        this.loadComparison()
      } else {
        this.loadSinglePage(this.selectedPageIds[0])
      }
    },

    loadSinglePage(pageId) {
      const es = new EventSource(this.buildEndpoint(pageId))
      this.activeEventSources.push(es)

      es.onerror = () => {
        this.loading = false
        this.progressPercent = 100
        es.close()
      }

      es.onmessage = (event) => {
        const data = JSON.parse(event.data)

        if (data.queued) {
          this.loading = false
          this.progressPercent = 0
          this.progressDay = null
          es.close()
          this.backgroundifySinglePage(pageId, this.buildEndpoint(pageId))
          return
        }

        if (data.daysDone != null) {
          this.progressPercent = data.total
            ? Math.min(90, Math.round((data.daysDone / data.total) * 90))
            : 0
          return
        }

        if (data.stats) {
          this.currentStats = data.stats
          this.graphLabels = (data.stats.page.nb_post_graph || [])
            .sort((a, b) => a.date - b.date)
            .map(p => moment(p.date * 1000).format(this.localeDateLong))
          this.loading = false
          this.progressPercent = 100
          this.progressDay = null
          es.close()
          if (this.compareMode !== 'none') this.loadPreviousPeriod(pageId)
        }
      }
    },

    buildPrevEndpoint(pageId) {
      const { VUE_APP_API_HOST } = process.env
      const days = this.periodDays
      const mode = this.effectiveCompareMode
      let compStart, compEnd
      if (mode === 'month') {
        compStart = moment(this.dateStart).subtract(1, 'month')
        compEnd   = moment(this.dateEnd).subtract(1, 'month')
      } else if (mode === 'year') {
        compStart = moment(this.dateStart).subtract(1, 'year')
        compEnd   = moment(this.dateEnd).subtract(1, 'year')
      } else {
        compEnd   = moment(this.dateStart).subtract(1, 'day')
        compStart = compEnd.clone().subtract(days - 1, 'days')
      }
      const tz = new Date().getTimezoneOffset()
      return `${VUE_APP_API_HOST}/reporting?pageid=${pageId}&since=${compStart.unix()}&until=${compEnd.unix()}&timezone=${tz}&step=all&percent=true`
    },

    loadPreviousPeriod(pageId) {
      this.prevLoading = true
      const es = new EventSource(this.buildPrevEndpoint(pageId))
      this.activeEventSources.push(es)

      es.onerror = () => { this.prevLoading = false; es.close() }

      es.onmessage = (event) => {
        const data = JSON.parse(event.data)
        if (data.stats) {
          this.prevLoading = false
          const p = data.stats.page
          const fanAddsArr = p.page_fan_adds_unique || p.page_fan_adds || []
          const fanAdds = Array.isArray(fanAddsArr)
            ? fanAddsArr.reduce(function(s, d) { return s + (d.total || 0) }, 0)
            : (fanAddsArr || 0)
          const pageViewsArr = p.page_views_total
          const pageViews = pageViewsArr == null ? null
            : Array.isArray(pageViewsArr)
              ? pageViewsArr.reduce(function(s, d) { return s + (d.total || 0) }, 0)
              : pageViewsArr
          const imp = p.totalImpressions || 0
          const eng = p.totalEngagement || 0
          const prevPeriod = {
            page_fans:        p.page_fans       || 0,
            nb_posts:         p.nb_posts        || 0,
            totalImpressions: imp,
            totalEngagement:  eng,
            totalReactions:   p.totalReactions  || 0,
            totalComments:    p.totalComments   || 0,
            totalShares:      p.totalShares     || 0,
            totalClicks:      p.totalClicks     != null ? p.totalClicks : null,
            page_fan_adds:    fanAdds,
            page_views_total: pageViews,
            engagement_rate:  imp > 0 ? parseFloat((eng / imp * 100).toFixed(2)) : 0,
          }
          if (this.currentStats && this.currentStats.page) {
            this.$set(this.currentStats.page, 'previous_period', prevPeriod)
          }
          es.close()
        }
      }
    },

    loadComparison() {
      const results = {}
      let completed = 0
      const total = this.selectedPageIds.length

      this.selectedPageIds.forEach(pageId => {
        const es = new EventSource(this.buildEndpoint(pageId))
        this.activeEventSources.push(es)

        es.onerror = () => {
          completed++
          if (completed === total) this.finalizeComparison(results)
          es.close()
        }

        es.onmessage = (event) => {
          const data = JSON.parse(event.data)

          if (data.queued) {
            es.close()
            completed++
            if (completed === total) this.finalizeComparison(results)
            this.backgroundifyComparisonPage(pageId, this.buildEndpoint(pageId))
            return
          }

          if (data.stats) {
            results[pageId] = { page: this.allPages[pageId], stats: data.stats }
            completed++
            this.progressPercent = Math.round((completed / total) * 100)
            if (completed === total) this.finalizeComparison(results)
            es.close()
          }
        }
      })
    },

    finalizeComparison(results) {
      this.pageStats = this.selectedPageIds.map(id => results[id]).filter(Boolean)
      this.loading = false
      this.progressDay = null
      if (this.compareMode !== 'none') {
        this.pageStats.forEach(entry => this.loadComparisonPreviousPeriod(entry))
      }
      this.loadConcurrentsForAllPages()
    },

    async loadConcurrentsForAllPages() {
      this.concurrentsLoading = true
      const apiHost = process.env.VUE_APP_API_HOST
      const token = localStorage.getItem('token')
      const since = moment(this.dateStart).unix()
      const until = moment(this.dateEnd).unix()
      const cache = {}
      await Promise.all(this.pageStats.map(async entry => {
        const page = entry.page
        const stats = entry.stats
        if (!page || !page.reporting || !page.reporting.concurrents || !page.reporting.concurrents.length) return
        try {
          const url = `${apiHost}/reporting/concurrent/data?page_id=${page.id}&since=${since}&until=${until}`
          const res = await fetch(url, { headers: { Authorization: token ? `Bearer ${token}` : '' } })
          const json = await res.json()
          if (!res.ok) return
          const freshAvatar = (network, networkId, fallback) =>
            (network === 'facebook' || network === 'instagram') && networkId
              ? `https://graph.facebook.com/${networkId}/picture?type=large`
              : (fallback || null)
          const rows = (json.concurrents || []).filter(c => !c.pending)
            .map(c => Object.assign({}, c, { concurrent_picture: freshAvatar(c.network || page.network, c.network_id, c.concurrent_picture) }))
            .sort((a, b) => (b.pageFollowers || b.pageFans || 0) - (a.pageFollowers || a.pageFans || 0))
          if (!rows.length) return
          const p = stats && stats.page ? stats.page : {}
          const ownRow = {
            network_id: page.network_id || page.id,
            concurrent_name: page.name,
            concurrent_picture: freshAvatar(page.network, page.network_id, page.avatar),
            pageFollowers: p.page_fans || p.followers_count || 0,
            postsCount: p.nb_posts || 0,
            total_reactions: p.totalReactions || 0,
            total_comments: p.totalComments || 0,
            total_shares: p.totalShares || 0,
            posts: null,
            isOwnPage: true,
          }
          // Tri global fans décroissant — own page dans le classement réel, pas forcé en tête
          cache[page.id] = [ownRow, ...rows].sort(
            (a, b) => (b.pageFollowers || b.pageFans || 0) - (a.pageFollowers || a.pageFans || 0)
          )
        } catch (e) { /* skip on error */ }
      }))
      this.concurrentDataCache = cache
      this.concurrentsLoading = false
    },

    loadComparisonPreviousPeriod(entry) {
      this.prevLoading = true
      const pageId = entry.page.id
      const es = new EventSource(this.buildPrevEndpoint(pageId))
      this.activeEventSources.push(es)
      es.onerror = () => { es.close() }
      es.onmessage = (event) => {
        const data = JSON.parse(event.data)
        if (data.stats) {
          const p = data.stats.page
          const fanAddsArr = p.page_fan_adds_unique || p.page_fan_adds || []
          const fanAdds = Array.isArray(fanAddsArr)
            ? fanAddsArr.reduce(function(s, d) { return s + (d.total || 0) }, 0)
            : (fanAddsArr || 0)
          const pageViewsArr = p.page_views_total
          const pageViews = pageViewsArr == null ? null
            : Array.isArray(pageViewsArr)
              ? pageViewsArr.reduce(function(s, d) { return s + (d.total || 0) }, 0)
              : pageViewsArr
          const ss = (v) => v == null ? null : (Array.isArray(v) ? v.reduce((s, d) => s + (d.total || 0), 0) : v)
          const imp = p.totalImpressions || 0
          const eng = p.totalEngagement || 0
          const prevPeriod = {
            page_fans:        p.page_fans      || 0,
            nb_posts:         p.nb_posts       || 0,
            totalImpressions: imp,
            totalEngagement:  eng,
            totalReactions:   p.totalReactions || 0,
            totalComments:    p.totalComments  || 0,
            totalShares:      p.totalShares    || 0,
            totalClicks:      p.totalClicks    != null ? p.totalClicks : null,
            page_fan_adds:    fanAdds,
            page_views_total: pageViews,
            engagement_rate:  imp > 0 ? parseFloat((eng / imp * 100).toFixed(2)) : 0,
            totalReach:                 ss(p.totalReach),
            page_impressions_organic:   ss(p.page_impressions_organic_v2) != null ? ss(p.page_impressions_organic_v2) : ss(p.page_impressions_organic),
            page_impressions_organic_v2: ss(p.page_impressions_organic_v2),
            page_impressions_paid:      ss(p.page_impressions_paid),
            page_impressions_viral:     ss(p.page_impressions_viral),
            totalUniqueImpressions:     p.totalUniqueImpressions != null ? p.totalUniqueImpressions : null,
          }
          if (entry.stats && entry.stats.page) {
            this.$set(entry.stats.page, 'previous_period', prevPeriod)
          }
          const allDone = this.pageStats.every(e => e.stats && e.stats.page && e.stats.page.previous_period)
          if (allDone) this.prevLoading = false
          es.close()
        }
      }
    },

    // Charge uniquement les pages manquantes et les ajoute à pageStats
    fetchPages(pageIds) {
      if (!pageIds.length) return
      this.loading = true
      this.progressPercent = 0
      this.progressDay = null
      const total = pageIds.length

      // Progression individuelle par page (0.0 → 1.0)
      const pageProgress = {}
      pageIds.forEach(id => { pageProgress[id] = 0 })

      const updateOverall = () => {
        const sum = Object.values(pageProgress).reduce((s, v) => s + v, 0)
        // 0–90 % pendant le chargement des jours, 100 % uniquement à la réception des stats
        this.progressPercent = Math.min(90, Math.round((sum / total) * 90))
      }

      // Pages basculées en RequestManager (plus suivies par le loader inline)
      const backgroundified = new Set()

      const checkAllSettled = () => {
        const remaining = pageIds.filter(id => !backgroundified.has(id) && (pageProgress[id] || 0) < 1)
        if (remaining.length > 0) return
        stopFake()
        this.progressPercent = backgroundified.size === pageIds.length ? 0 : 100
        this.progressDay = null
        this.loading = false
      }

      const markDone = (pageId) => {
        stopFake()
        pageProgress[pageId] = 1
        checkAllSettled()
      }

      const promoteToBackground = (pageId, es) => {
        es.close()
        backgroundified.add(pageId)
        delete pageProgress[pageId]
        // Relance via RequestManager — le back retrouvera les jours déjà en cache ES
        this._backgroundifyPage(pageId, this.buildEndpoint(pageId))
        if (Object.keys(pageProgress).length === 0) {
          stopFake()
          this.loading = false
          this.progressPercent = 0
        } else {
          updateOverall()
        }
      }

      pageIds.forEach(pageId => {
        const es = new EventSource(this.buildEndpoint(pageId))
        this.activeEventSources.push(es)

        es.onerror = () => { markDone(pageId); es.close() }

        es.onmessage = (event) => {
          const data = JSON.parse(event.data)

          // Jours manquants → bascule cette page en RequestManager
          if (data.queued) {
            promoteToBackground(pageId, es)
            return
          }

          if (data.daysDone != null && data.total) {
            pageProgress[pageId] = data.daysDone / data.total
            updateOverall()
            return
          }

          if (data.stats) {
            const entry = { page: this.allPages[pageId], stats: data.stats }
            const idx = this.pageStats.findIndex(e => e.page.id === pageId)
            if (idx >= 0) this.$set(this.pageStats, idx, entry)
            else          this.pageStats.push(entry)
            markDone(pageId)
            es.close()
          }
        }
      })
    },

    _backgroundifyPage(pageId, endpoint) {
      const page = this.allPages[pageId]
      const dateStart = this.dateStart
      const dateEnd = this.dateEnd
      const periodLabel = moment(dateStart).format('DD/MM') + ' → ' + moment(dateEnd).format('DD/MM')

      const cbStartRequest = (onSuccess, onProgress, onError) => {
        return new Promise((resolve, reject) => {
          const es = new EventSource(endpoint)
          es.onerror = () => { es.close(); onError(); reject() }
          es.onmessage = (event) => {
            const data = JSON.parse(event.data)
            if (data.error) { es.close(); onError(); reject(); return }
            if (data.daysDone != null) {
              const pct = data.total ? Math.min(90, Math.round((data.daysDone / data.total) * 90)) : 0
              const dayLabel = data.day
                ? ' · ' + this.$t('dash.reporting.fetching_day', { date: moment(data.day).format('DD/MM') })
                : ''
              onProgress(pct + '%' + dayLabel)
              return
            }
            if (data.stats) {
              es.close()
              onSuccess('Prêt · ' + periodLabel)
              resolve()
            }
          }
        })
      }

      const onAction = () => {
        const route = this.$router.resolve({
          name: 'reporting',
          query: { since: dateStart, until: dateEnd, pageIds: String(pageId) },
        })
        window.open(route.href, '_blank')
      }

      EventBus.$emit('requestManagerAdd', cbStartRequest, {
        title: (page ? page.name : pageId) + ' — reporting',
        picture: page ? page.avatar : null,
        loadingText: 'Connexion...',
        successText: 'Prêt · ' + periodLabel,
        onAction,
        autoOpen: true,
      })
    },

    backgroundifySinglePage(pageId, endpoint) {
      this._backgroundifyPage(pageId, endpoint)
    },

    backgroundifyComparisonPage(pageId, endpoint) {
      this._backgroundifyPage(pageId, endpoint)
    },

    closeExportDropdown() {
      this.exportDropdownOpen = false
    },

    runExport(type) {
      this.exportDropdownOpen = false
      const stats = this.isComparisonMode ? this.detailStats : this.currentStats
      const page = this.isComparisonMode ? this.detailPage : this.currentPage
      if (!stats || !page) return
      if (type === 'pdf') {
        this.isComparisonMode
          ? this.exportPdf(this.detailStats, this.detailPage, this.detailGraphLabels)
          : this.exportPdf()
      } else if (type === 'csv') {
        this.exportCsv(stats, page)
      } else if (type === 'excel') {
        this.exportExcel(stats, page)
      } else if (type === 'api') {
        this.openApiUrlModal(page)
      }
    },

    _buildPostsRows(stats, page) {
      const network = page ? page.network : ''
      const posts = (stats.posts || []).slice().sort((a, b) => {
        const ta = a.created_time || a.date || 0
        const tb = b.created_time || b.date || 0
        return ta < tb ? 1 : ta > tb ? -1 : 0
      })

      const baseHeaders = ['Date', 'Type', 'Message', 'URL']
      const metricHeaders = network === 'tiktok'
        ? ['Vues', 'Likes', 'Commentaires', 'Partages', 'Taux engagement (%)', 'Durée (s)', 'Temps regardé (s)', 'Taux complétion (%)']
        : network === 'linkedin'
          ? ['Impressions', 'Likes', 'Commentaires', 'Partages', 'Clics', 'Interactions']
          : ['Impressions', 'Likes', 'Commentaires', 'Partages', 'Taux engagement (%)']

      const headers = [...baseHeaders, ...metricHeaders]

      const rows = posts.map(p => {
        const base = [
          p.created_time ? moment(p.created_time).format('YYYY-MM-DD HH:mm') : '',
          p.type || '',
          p.message || p.video_description || '',
          p.permalink_url || '',
        ]
        let metrics
        if (network === 'tiktok') {
          metrics = [
            p.post_impressions || p.view_count || 0,
            p.like_count || 0,
            p.comments_count || 0,
            p.shares_count || 0,
            p.reach_engagement != null ? parseFloat(p.reach_engagement).toFixed(2) : '',
            p.duration || '',
            p.time_watched || '',
            p.video_finish_rate != null ? (p.video_finish_rate * 100).toFixed(1) : '',
          ]
        } else if (network === 'linkedin') {
          metrics = [
            p.post_impressions || 0,
            p.like_count || 0,
            p.comments_count || 0,
            p.shares_count || 0,
            p.click_count || 0,
            p.interactions || 0,
          ]
        } else {
          metrics = [
            p.post_impressions || 0,
            p.post_reactions_sum || p.like_count || 0,
            p.comments_count || 0,
            p.shares_count || 0,
            p.reach_engagement != null ? parseFloat(p.reach_engagement).toFixed(2) : '',
          ]
        }
        return [...base, ...metrics]
      })

      return { headers, rows }
    },

    exportCsv(stats, page) {
      const { headers, rows } = this._buildPostsRows(stats, page)
      const escape = v => `"${String(v).replace(/"/g, '""')}"`
      const lines = [headers, ...rows].map(r => r.map(escape).join(','))
      const csv = '﻿' + lines.join('\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
      this._triggerDownload(blob, `reporting-${(page.name || 'page').replace(/[^a-z0-9]/gi, '-').toLowerCase()}-${this.dateStart}.csv`)
    },

    exportExcel(stats, page) {
      const { headers, rows } = this._buildPostsRows(stats, page)
      const pg = stats.page || {}
      const esc = v => String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      const tr = cells => `<tr>${cells.map(c => `<td>${esc(c)}</td>`).join('')}</tr>`
      const th = cells => `<tr>${cells.map(c => `<th style="background:#6B1F4D;color:#fff;font-weight:bold">${esc(c)}</th>`).join('')}</tr>`

      const summaryRows = [
        ['Page', page.name || ''],
        ['Réseau', page.network || ''],
        ['Période', `${this.dateStart} → ${this.dateEnd}`],
        ['Abonnés', pg.page_fans || ''],
        ['Publications', pg.nb_posts || ''],
        ['Impressions totales', pg.totalImpressions || ''],
        ['Engagement total', pg.totalEngagement || ''],
      ]

      const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
<head><meta charset="utf-8">
<xml><x:ExcelWorkbook><x:ExcelWorksheets>
<x:ExcelWorksheet><x:Name>Résumé</x:Name><x:WorksheetOptions><x:Selected/></x:WorksheetOptions></x:ExcelWorksheet>
<x:ExcelWorksheet><x:Name>Publications</x:Name></x:ExcelWorksheet>
</x:ExcelWorksheets></x:ExcelWorkbook></xml></head>
<body>
<table x:str data-sheets-name="Résumé">${summaryRows.map(r => tr(r)).join('')}</table>
<br>
<table x:str data-sheets-name="Publications">${th(headers)}${rows.map(r => tr(r)).join('')}</table>
</body></html>`

      const blob = new Blob(['﻿' + html], { type: 'application/vnd.ms-excel;charset=utf-8' })
      this._triggerDownload(blob, `reporting-${(page.name || 'page').replace(/[^a-z0-9]/gi, '-').toLowerCase()}-${this.dateStart}.xls`)
    },

    _triggerDownload(blob, filename) {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },

    async openApiUrlModal(page) {
      this.apiModal = { open: true, loading: true, url: null, error: null, copied: false }
      try {
        const { VUE_APP_API_HOST } = process.env
        const token = localStorage.getItem('token')
        const resp = await fetch(`${VUE_APP_API_HOST}/reporting/api/url?pageId=${page.id}`, {
          headers: { Authorization: token ? `Bearer ${token}` : '' }
        })
        const data = await resp.json()
        if (data && data.url) {
          this.apiModal.url = data.url
        } else {
          this.apiModal.error = this.$t('dash.reporting.api_url_error')
        }
      } catch {
        this.apiModal.error = this.$t('dash.reporting.api_url_error')
      } finally {
        this.apiModal.loading = false
      }
    },

    async copyApiUrl() {
      if (!this.apiModal.url) return
      try {
        await navigator.clipboard.writeText(this.apiModal.url)
      } catch {
        if (this.$refs.apiUrlInput) {
          this.$refs.apiUrlInput.select()
          document.execCommand('copy')
        }
      }
      this.apiModal.copied = true
      setTimeout(() => { this.apiModal.copied = false }, 2000)
    },

    async exportPdf(statsArg, pageArg, labelsArg) {
      const stats = statsArg || this.currentStats
      const page = pageArg || this.currentPage
      const labels = labelsArg || this.graphLabels
      if (!stats || !page) return

      // Collect sorted concurrent rows — wait for async load if needed
      const concRef = this.$refs.concurrentSection
      const concurrents = concRef ? await concRef.getConcurrentDataForPdf() : null

      this.pdfLoading = true
      try {
        const { VUE_APP_API_HOST } = process.env
        const token = localStorage.getItem('token')
        const resp = await fetch(`${VUE_APP_API_HOST}/reporting/pdf/v2`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : '',
          },
          body: JSON.stringify({
            stats,
            page,
            dateStart: this.dateStart,
            dateEnd: this.dateEnd,
            graphLabels: labels,
            locale: this.$i18n.locale,
            concurrents: concurrents && concurrents.length > 1 ? concurrents : null,
          }),
        })
        if (!resp.ok) throw new Error('PDF generation failed: ' + resp.status)
        const blob = await resp.blob()
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `reporting-${(page.name || 'page').replace(/[^a-z0-9]/gi, '-').toLowerCase()}-${this.dateStart}.pdf`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      } catch (e) {
        console.error('[PDF]', e)
      } finally {
        this.pdfLoading = false
      }
    },
  },

  mounted() {
    const q = this.$route && this.$route.query
    if (!q || (!q.since && !q.until && !q.pageIds)) return
    if (q.since) this.dateStart = q.since
    if (q.until) this.dateEnd = q.until
    if (!q.pageIds) { this.$nextTick(() => this.loadData(false)); return }

    const ids = String(q.pageIds).split(',').filter(Boolean)
    const applyAndLoad = () => {
      this.$store.commit('selectPage/updatePagesSelected', { pagesId: ids, saveToLocalStorage: false })
      this.$nextTick(() => this.loadData(false))
    }

    if (Object.keys(this.allPages).length) {
      applyAndLoad()
    } else {
      const unwatch = this.$watch('allPages', (pages) => {
        if (!Object.keys(pages).length) return
        unwatch()
        applyAndLoad()
      })
    }
  },

  beforeDestroy() {
    this.closeAllEventSources()
  },
}
</script>

<style scoped>
/* Prend toute la zone de la .section (position: relative dans dash.scss) */
.reporting-main {
  height: 100%;
  background: #f4f0f8;
  display: flex;
  flex-direction: column;
}

/* Contenu principal — scroll interne */
.reporting-content {
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  box-sizing: border-box;
}

/* Toolbar */
.reporting-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  background: #fff;
  border-radius: 5.6px !important;
  padding: 10px 16px;
  box-shadow: 0 2px 12px rgba(107,31,77,0.06);
  border: 1px solid #f1edf5;
  flex-shrink: 0;
  margin: 20px 24px 0;
  position: relative;
  z-index: 100;
}
.period-presets {
  display: flex;
  gap: 6px;
}
.preset-btn {
  padding: 5px 14px;
  border-radius: 20px;
  border: 1px solid #e0d0ea;
  background: #fff;
  color: #6B1F4D;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
}
.preset-btn:hover,
.preset-btn.active {
  background: #6B1F4D;
  color: #fff;
  border-color: #6B1F4D;
}
.date-range {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}
.date-sep { color: #9ba3b7; font-size: 16px; }
.date-input {
  padding: 5px 10px;
  border: 1px solid #e0d0ea;
  border-radius: 8px;
  font-size: 13px;
  color: #121826;
  background: #fff;
  cursor: pointer;
  outline: none;
  transition: border-color 0.15s;
}
.date-input:focus { border-color: #6B1F4D; }
.date-input::-webkit-calendar-picker-indicator { cursor: pointer; opacity: 0.6; }
.search-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 13px;
  border-radius: 8px;
  border: none;
  background: #6B1F4D;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;
}
.search-btn:hover:not(:disabled) { opacity: 0.88; }
.search-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.search-btn .material-icons { font-size: 16px; }
.refresh-btn {
  display: flex;
  align-items: center;
  padding: 5px 8px;
  border-radius: 8px;
  border: 1.5px solid #e0d0ea !important;
  background: #fff !important;
  color: #6B1F4D !important;
  cursor: pointer;
  transition: all 0.15s;
  flex-shrink: 0;
}
.refresh-btn:hover { background: #f1edf5; border-color: #6B1F4D; }
.refresh-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.refresh-btn .material-icons { font-size: 16px; }
.compare-phrase {
  font-size: 13px;
  color: #555e75;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.compare-select-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
}
.compare-select {
  appearance: none;
  -webkit-appearance: none;
  border: none;
  border-bottom: 2px solid #6B1F4D;
  background: transparent;
  color: #6B1F4D;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  padding: 0 18px 0 2px;
  outline: none;
}
.compare-select-arrow {
  position: absolute;
  right: 0;
  font-size: 18px;
  color: #6B1F4D;
  pointer-events: none;
  line-height: 1;
}

.export-dropdown {
  position: relative;
  flex-shrink: 0;
}
.export-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  border-radius: 8px;
  border: 1.5px solid #6B1F4D !important;
  background: transparent !important;
  color: #6B1F4D !important;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}
.export-btn:hover, .export-dropdown.open .export-btn { background: #6B1F4D !important; color: #fff !important; }
.export-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.export-btn .material-icons { font-size: 15px; }
.export-caret { font-size: 15px !important; margin-left: 2px; }
.export-menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: #fff;
  border: 1px solid #e0d0ea;
  border-radius: 5.6px !important;
  box-shadow: 0 6px 20px rgba(107,31,77,0.12);
  min-width: 160px;
  z-index: 200;
  overflow: hidden;
  padding: 4px 0;
}
.export-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 9px 14px;
  border: none;
  background: transparent !important;
  color: #121826 !important;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  transition: background 0.12s;
}
.export-item:hover { background: #f6f1fa !important; color: #6B1F4D !important; }
.export-item .material-icons { font-size: 15px; color: #9ba3b7 !important; transition: color 0.12s; }
.export-item:hover .material-icons { color: #6B1F4D !important; }
.export-separator { height: 1px; background: #f1edf5 !important; margin: 4px 0; }

/* Modal API URL */
.api-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.35);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}
.api-modal {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 8px 32px rgba(107,31,77,0.16);
  width: 520px;
  max-width: 95vw;
  overflow: hidden;
}
.api-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #f1edf5;
  font-size: 15px;
  font-weight: 700;
  color: #121826;
}
.api-modal-close {
  border: none;
  background: transparent;
  cursor: pointer;
  color: #9ba3b7;
  display: flex;
  align-items: center;
  padding: 2px;
  border-radius: 6px;
  transition: background 0.12s;
}
.api-modal-close:hover { background: #f1edf5; color: #6B1F4D; }
.api-modal-body { padding: 18px 20px 22px; }
.api-modal-desc { font-size: 13px; color: #555e75; margin-bottom: 14px; }
.api-modal-loading { display: flex; justify-content: center; padding: 20px 0; }
.api-modal-error { color: #c0392b; font-size: 13px; }
.api-url-field {
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1.5px solid #e0d0ea;
  border-radius: 8px;
  padding: 4px 4px 4px 10px;
  background: #faf8fc;
}
.api-url-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 12px;
  color: #555e75;
  outline: none;
  font-family: monospace;
  min-width: 0;
}
.api-url-copy {
  display: flex;
  align-items: center;
  padding: 5px 8px;
  border: none;
  background: #6B1F4D;
  color: #fff;
  border-radius: 6px;
  cursor: pointer;
  flex-shrink: 0;
  transition: opacity 0.15s;
}
.api-url-copy:hover { opacity: 0.85; }
.api-url-copy .material-icons { font-size: 16px; }
.api-url-hint { font-size: 11px; color: #9ba3b7; margin-top: 8px; }
@keyframes spin { to { transform: rotate(360deg); } }
.spin { display: inline-block; animation: spin 1s linear infinite; }

/* Progress */
.reporting-progress {
  background: #fff;
  border-radius: 12px;
  padding: 14px 18px;
  border: 1px solid #f1edf5;
  flex-shrink: 0;
}
.progress-bar-track {
  height: 6px;
  border-radius: 3px;
  background: #f1edf5;
  overflow: hidden;
}
.progress-bar-fill {
  height: 100%;
  background: #6B1F4D;
  border-radius: 3px;
  transition: width 0.3s ease;
}
.progress-label {
  font-size: 12px;
  color: #9ba3b7;
  margin-top: 6px;
  display: block;
}

/* Empty */
.reporting-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #9ba3b7;
}
.reporting-empty .material-icons { font-size: 48px; color: #e0d0ea; }
.reporting-empty p { font-size: 15px; }

/* Barre d'onglets comparaison */
.comparison-tab-bar {
  display: flex;
  align-items: center;
  gap: 4px;
  background: #fff;
  border-radius: 5.6px !important;
  padding: 8px 10px;
  box-shadow: 0 2px 12px rgba(107,31,77,0.06);
  border: 1px solid #f1edf5;
  flex-shrink: 0;
  overflow-x: auto;
  scrollbar-width: none;
}
.comparison-tab-bar::-webkit-scrollbar { display: none; }
.ctab {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  border-radius: 5.6px;
  cursor: pointer;
  font-size: 13px;
  color: #555e75;
  font-weight: 500;
  transition: all 0.15s;
  white-space: nowrap;
  flex-shrink: 0;
  user-select: none;
}
.ctab:hover { background: #f6f1fa; color: #6B1F4D; }
.ctab.active { background: #6B1F4D; color: #fff; }
.ctab-icon { font-size: 17px; }
.ctab-sep {
  width: 1px;
  height: 28px;
  background: #e0d0ea;
  flex-shrink: 0;
  margin: 0 4px;
}
.ctab-avatar-wrap {
  position: relative;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
}
.ctab-avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  object-fit: cover;
  display: block;
}
.ctab-avatar-fallback {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #f1edf5;
  color: #6B1F4D;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
}
.ctab.active .ctab-avatar-fallback { background: rgba(255,255,255,0.25); color: #fff; }
.ctab-net-badge {
  position: absolute;
  bottom: -2px;
  right: -3px;
  width: 13px;
  height: 13px;
  border-radius: 50%;
  border: 1.5px solid #fff;
  object-fit: cover;
}
.ctab.active .ctab-net-badge { border-color: #6B1F4D; }
</style>
